import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { downloadFileFromUrl } from "@piiqtechnologies/ui-lib";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { useRequestReportExport } from "@hooks/useRequestReportExport";
import { ExportStatusBanner } from "@components/common/export-status-banner";
import { ExportErrorReason, ExportKind, ExportStatus } from "@constants/Export";
const SUCCESS_AUTO_DISMISS_MS = 5000;
const buildEventProps = (payload) => {
    var _a;
    return (Object.assign({ format: payload.format, communityCount: payload.communityIds.length, mode: payload.mode, kind: (_a = payload.kind) !== null && _a !== void 0 ? _a : ExportKind.Reports }, (payload.organizationId ? { organizationId: payload.organizationId } : {})));
};
const serverMessageFor = (error) => {
    var _a;
    const code = (_a = error === null || error === void 0 ? void 0 : error.cause) === null || _a === void 0 ? void 0 : _a.code;
    return code === 409 ? (error === null || error === void 0 ? void 0 : error.message) || undefined : undefined;
};
const ExportContext = createContext(undefined);
export const ExportProvider = ({ children }) => {
    const [status, setStatus] = useState(ExportStatus.Idle);
    const [includedCount, setIncludedCount] = useState(0);
    const [skippedCount, setSkippedCount] = useState(0);
    const [errorReason, setErrorReason] = useState(ExportErrorReason.Generic);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const requestExport = useRequestReportExport();
    const track = useTrackEvent();
    const lastPayloadRef = useRef(null);
    const requestIdRef = useRef(0);
    const run = useCallback((payload) => {
        lastPayloadRef.current = payload;
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        const props = buildEventProps(payload);
        setStatus(ExportStatus.Preparing);
        requestExport(payload)
            .then(({ url, fileName, included, skippedSubjectIds }) => {
            var _a;
            if (requestIdRef.current !== requestId)
                return;
            const skipped = (_a = skippedSubjectIds === null || skippedSubjectIds === void 0 ? void 0 : skippedSubjectIds.length) !== null && _a !== void 0 ? _a : 0;
            setIncludedCount(included !== null && included !== void 0 ? included : 0);
            setSkippedCount(skipped);
            setErrorMessage(undefined);
            if (included === 0) {
                setErrorReason(ExportErrorReason.NothingToExport);
                setStatus(ExportStatus.Error);
                track(AnalyticsArea.Export, "Export Failed", Object.assign(Object.assign({}, props), { reason: ExportErrorReason.NothingToExport }));
                return;
            }
            setErrorReason(ExportErrorReason.Generic);
            setStatus(ExportStatus.Success);
            downloadFileFromUrl(url, fileName);
            track(AnalyticsArea.Export, "Export Succeeded", skipped ? Object.assign(Object.assign({}, props), { skippedCount: skipped }) : props);
        })
            .catch((error) => {
            if (requestIdRef.current !== requestId)
                return;
            setErrorReason(ExportErrorReason.Generic);
            setErrorMessage(serverMessageFor(error));
            setStatus(ExportStatus.Error);
            track(AnalyticsArea.Export, "Export Failed", props);
        });
    }, [requestExport, track]);
    const startExport = useCallback((payload) => {
        track(AnalyticsArea.Export, "Export Started", buildEventProps(payload));
        run(payload);
    }, [run, track]);
    const retry = useCallback(() => {
        const payload = lastPayloadRef.current;
        if (!payload)
            return;
        track(AnalyticsArea.Export, "Export Retried", buildEventProps(payload));
        run(payload);
    }, [run, track]);
    const dismiss = useCallback(() => {
        requestIdRef.current += 1;
        setStatus(ExportStatus.Idle);
    }, []);
    useEffect(() => {
        if (status !== ExportStatus.Success)
            return undefined;
        const timer = setTimeout(() => setStatus(ExportStatus.Idle), SUCCESS_AUTO_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [status]);
    const contextValue = useMemo(() => ({ status, isExporting: status === ExportStatus.Preparing, startExport, retry, dismiss }), [status, startExport, retry, dismiss]);
    return (React.createElement(ExportContext.Provider, { value: contextValue },
        children,
        React.createElement(ExportStatusBanner, { status: status, onRetry: retry, onDismiss: dismiss, includedCount: includedCount, skippedCount: skippedCount, errorReason: errorReason, errorMessage: errorMessage })));
};
export const useExportContext = () => {
    const context = useContext(ExportContext);
    if (context === undefined) {
        throw new Error("useExportContext must be used within an ExportProvider");
    }
    return context;
};
export default ExportProvider;
